/**
 * Data layer for `custom-exchange-rate-per-invoice`. Thin typed fetch wrappers over /api/v1/custom-exchange-rate-per-invoice.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CustomExchangeRatePerInvoiceRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCustomExchangeRatePerInvoiceResponse {
	count: number;
	items: CustomExchangeRatePerInvoiceRow[];
}

export interface CreateCustomExchangeRatePerInvoiceBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/custom-exchange-rate-per-invoice";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCustomExchangeRatePerInvoiceResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomExchangeRatePerInvoiceResponse>(res);
}

export async function create(body: CreateCustomExchangeRatePerInvoiceBody): Promise<CustomExchangeRatePerInvoiceRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomExchangeRatePerInvoiceRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customExchangeRatePerInvoiceQueryKeys = {
	all: ["custom-exchange-rate-per-invoice"] as const,
	list: () => [...customExchangeRatePerInvoiceQueryKeys.all] as const,
	detail: (id: string) => [...customExchangeRatePerInvoiceQueryKeys.all, id] as const,
};
